import { useQuery } from '@tanstack/react-query';
import { http } from '@lib/api';
import { queryKeys } from '@lib/queryKeys';
import type { components } from '@/types/api.generated';

// The dispatch preview is a SUMMARY (#935): planned/dispatchable counts plus
// a list of anomalies (a job that can't dispatch because its order is gone).
export type DispatchPreview = components['schemas']['DispatchPreview'];

/**
 * #2475: ONE standing consolidated dry-run per date. Its `plannedTotal` and
 * `anomalies` do not depend on the merge switch, so the switch-off count (one
 * driver task per delivery job) derives from it too, and flipping the switch
 * needs no request. Keyed on the date with no `placeholderData`: a date change
 * shows the loading state (run disabled), never a count that belongs to
 * another day — the run creates real Onfleet tasks (#1772). The page, its run
 * card and today's focus cards (#2485) all read it through this hook, so one
 * key has one `queryFn`. `http.get` unwraps the `{ data }` envelope.
 */
export function useDispatchPreview(date: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.dispatch.preview(date, true),
    queryFn: () =>
      http.get<DispatchPreview>('/api/dispatch/preview', {
        params: { date, consolidate: 'true' },
      }),
    staleTime: 60 * 1000,
    enabled: options?.enabled ?? true,
  });
}
